import {
  MenuItem as HeadlessMenuItem,
  type MenuItemProps as HeadlessMenuItemProps,
} from "@headlessui/react";
import type { LinkProps } from "react-router";
import { classNames } from "../../utils/classNames";
import { Link } from "../Link";

type DropdownItemButtonProps = (
  | ({
      to?: never;
    } & HeadlessMenuItemProps<"button">)
  | ({
      to: LinkProps["to"];
    } & HeadlessMenuItemProps<typeof Link>)
) & {
  icon?: "end" | "start";
};

export function DropdownItemButton({
  icon = "start",
  ...props
}: DropdownItemButtonProps) {
  return (
    <HeadlessMenuItem
      as={props.to ? Link : (props.as ?? "button")}
      type={props.to ? undefined : "button"}
      {...props}
      className={classNames(
        // Base styles
        "group rounded-lg px-3.5 py-2.5 focus:outline-none sm:px-4 sm:py-2 cursor-pointer",

        // Text styles
        "text-left text-base/6 text-primary sm:text-sm/6 font-medium forced-colors:text-[CanvasText]",

        // Focus
        "data-[focus]:bg-menu-hover",

        // Disabled state
        "data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed",

        // Forced colors mode
        "forced-color-adjust-none forced-colors:data-[focus]:bg-[Highlight] forced-colors:data-[focus]:text-[HighlightText] forced-colors:[&>[data-slot=icon]]:data-[focus]:text-[HighlightText]",

        // Use subgrid when available but fallback to an explicit grid layout if not
        "col-span-full grid grid-cols-[auto_1fr_1.5rem_0.5rem_auto] items-center #supports-[grid-template-columns:subgrid]:grid-cols-subgrid",

        // Icon
        {
          "[&>[data-slot=icon]]:size-5 [&>[data-slot=icon]]:sm:size-4": true,
          "[&>[data-slot=icon]]:text-zinc-500 [&>[data-slot=icon]]:dark:text-zinc-500 [&>[data-slot=icon]]:data-[focus]:dark:text-white": true,
          "[&>[data-slot=icon]]:col-start-1 [&>[data-slot=icon]]:row-start-1 [&>[data-slot=icon]]:mr-2.5 sm:[&>[data-slot=icon]]:mr-3":
            icon === "start",
          "[&>[data-slot=icon]]:col-start-3 [&>[data-slot=icon]]:row-start-1 [&>[data-slot=icon]]:ml-2.5 sm:[&>[data-slot=icon]]:ml-3":
            icon === "end",
        },

        "[&>[data-slot=avatar]]:size-8 [&>[data-slot=avatar]]:col-start-1 [&>[data-slot=avatar]]:row-start-1 [&>[data-slot=avatar]]:mr-2.5 sm:[&>[data-slot=avatar]]:mr-3",

        props.className,
      )}
    />
  );
}
